import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Separator } from '../../../../../../../CommonComponents/OPL/Separator/Separator';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../../../../../CommonComponents/OPL/Input/Input';
import {
  FormValues,
  LifeExpectancyOptionsType,
  Options,
  OptionsDictionary,
} from './EstatePlanningLifeExpectancies.meta';
import { Card } from '../../../../../../../CommonComponents/OPL/Card/Card';

interface Props {
  options: Options;
}

export const EstatePlanningLifeExpectancies = ({ options }: Props) => {
  const formContext = useFormContext<FormValues>();

  const principalOptions = Object.keys(options.LifeExpectancyPrincipalOptions).map(option => ({
    value: OptionsDictionary[option].value,
    label: OptionsDictionary[option].label,
  }));

  const principalLifeExpectancyOptions = options.LifeExpectancyPrincipalOptions.LifeExpectancy.map(
    option => ({ value: option, label: option }),
  );

  const spouseOptions = Object.keys(options.LifeExpectancySpouseOptions).map(option => ({
    value: OptionsDictionary[option].value,
    label: OptionsDictionary[option].label,
  }));

  const spouseLifeExpectancyOptions = (
    options.LifeExpectancySpouseOptions.LifeExpectancy || []
  ).map(option => ({ value: option, label: option }));

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = formContext;

  const lifeExpectancyTypePrincipal = watch('LifeExpectancyTypePrincipal');
  const lifeExpectancyTypeSpouse = watch('LifeExpectancyTypeSpouse');

  return (
    <>
      <Text fontSize="h5" fontWeight={600} px="12" py="16">
        What-if...
      </Text>

      <Separator />

      <Box p={12}>
        <Text fontSize="h6" fontWeight={500} mb="16">
          General
        </Text>

        <Card mb={16} hasBorder p="8">
          <Controller
            name="LifeExpectancyTypePrincipal"
            control={control}
            render={({ field: { value, onChange, ref, ...field } }) => (
              <Select
                label={`${options.PrincipalName}’s Life Expectancy Options`}
                options={principalOptions}
                innerRef={ref}
                isPortal
                value={principalOptions.find(option => option.value === value)}
                onChange={option => {
                  onChange(option?.value);
                }}
                mb="16"
                {...field}
              />
            )}
          />

          {lifeExpectancyTypePrincipal === LifeExpectancyOptionsType.LifeExpectancy && (
            <>
              <Controller
                name="LifeExpectancyPrincipal"
                control={control}
                render={({ field: { value, onChange, ref, ...field } }) => (
                  <Select
                    label={`${options.PrincipalName}’s age`}
                    options={principalLifeExpectancyOptions}
                    innerRef={ref}
                    isPortal
                    value={principalLifeExpectancyOptions.find(option => {
                      return option.value === +value;
                    })}
                    onChange={option => {
                      onChange(option?.value);
                    }}
                    {...field}
                  />
                )}
              />
            </>
          )}

          {lifeExpectancyTypePrincipal === LifeExpectancyOptionsType.RemainingYearsUntilPassing && (
            <>
              <Input
                {...register('RemainingYearsUntilPassingPrincipal')}
                label={`${options.PrincipalName} Remaining Years Until Passing`}
                formVariant={errors.RemainingYearsUntilPassingPrincipal && 'error'}
                helperText={errors.RemainingYearsUntilPassingPrincipal?.message}
                type="number"
                maxLength={3}
              />
            </>
          )}
        </Card>

        {options.SpouseName && (
          <Card hasBorder p="8">
            <Controller
              name="LifeExpectancyTypeSpouse"
              control={control}
              render={({ field: { value, onChange, ref, ...field } }) => (
                <Select
                  label={`${options.SpouseName}’s Life Expectancy Options`}
                  options={spouseOptions}
                  innerRef={ref}
                  isPortal
                  value={spouseOptions.find(option => option.value === value)}
                  onChange={option => {
                    onChange(option?.value);
                  }}
                  mb="16"
                  {...field}
                />
              )}
            />

            {lifeExpectancyTypeSpouse === LifeExpectancyOptionsType.LifeExpectancy && (
              <>
                <Controller
                  name="LifeExpectancySpouse"
                  control={control}
                  render={({ field: { value, onChange, ref, ...field } }) => (
                    <Select
                      label={`${options.SpouseName}’s age`}
                      options={spouseLifeExpectancyOptions}
                      innerRef={ref}
                      isPortal
                      value={spouseLifeExpectancyOptions.find(option => {
                        return option.value === +value;
                      })}
                      onChange={option => {
                        onChange(option?.value);
                      }}
                      {...field}
                    />
                  )}
                />
              </>
            )}

            {lifeExpectancyTypeSpouse === LifeExpectancyOptionsType.RemainingYearsUntilPassing && (
              <>
                <Input
                  {...register('RemainingYearsUntilPassingSpouse')}
                  label={`${options.SpouseName} Remaining Years Until Passing`}
                  formVariant={errors.RemainingYearsUntilPassingSpouse && 'error'}
                  helperText={errors.RemainingYearsUntilPassingSpouse?.message}
                  type="number"
                  maxLength={3}
                />
              </>
            )}
          </Card>
        )}
      </Box>
    </>
  );
};
